"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
  Collapse,
  Button,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Mail as MailIcon,
  DoneAll as DoneAllIcon,
} from "@mui/icons-material";
import { UrlApi } from "../../services/apirest";
import axios from "axios";
import moment from "moment";
import Navbar from "../Navbar";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitial(name) {
  return (name ?? "?")[0].toUpperCase();
}

function relativeDate(fecha) {
  const m = moment(fecha);
  const diffDays = moment().diff(m, "days");
  if (diffDays === 0) return "Hoy";
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return m.format("DD/MM/YYYY");
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "unread", label: "No leídos" },
  { key: "read", label: "Leídos" },
];

// ─── EmailTable ───────────────────────────────────────────────────────────────

export default function EmailTable() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [mensajes, setMensajes] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchMensajes();
  }, []);

  const fetchMensajes = async () => {
    const data = await fetch(
      UrlApi + "/mensajes/" + localStorage.getItem("user") + "/todos"
    );
    const json = await data.json();
    setMensajes(Array.isArray(json) ? json : []);
  };

  const toggleReadStatus = async (id) => {
    await axios.put(UrlApi + "/mensajes/leido/" + id);
    fetchMensajes();
  };

  const deleteMensaje = async (id) => {
    await axios.delete(UrlApi + "/mensajes/" + id);
    if (expandedId === id) setExpandedId(null);
    fetchMensajes();
  };

  const markAllRead = async () => {
    const unread = mensajes.filter((m) => Number(m.estado) === 0);
    await Promise.all(unread.map((m) => axios.put(UrlApi + "/mensajes/leido/" + m.mensajes_id)));
    fetchMensajes();
  };

  const filtered = mensajes.filter((m) => {
    if (activeFilter === "unread") return Number(m.estado) === 0;
    if (activeFilter === "read") return Number(m.estado) === 1;
    return true;
  });

  const unreadCount = mensajes.filter((m) => Number(m.estado) === 0).length;

  const handleRowClick = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
    const msg = mensajes.find((m) => m.mensajes_id === id);
    if (msg && Number(msg.estado) === 0) {
      toggleReadStatus(id);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <Navbar />

      <Box sx={{ maxWidth: 900, mx: "auto", width: "100%", px: 2, py: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#0f172a">
              Bandeja de entrada
            </Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {unreadCount} mensaje{unreadCount !== 1 ? "s" : ""} sin leer
              </Typography>
            )}
          </Box>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={markAllRead}
              variant="outlined"
              sx={{ fontSize: 12 }}
            >
              Marcar todo como leído
            </Button>
          )}
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          {FILTERS.map(({ key, label }) => {
            const count =
              key === "all"
                ? mensajes.length
                : key === "unread"
                ? mensajes.filter((m) => Number(m.estado) === 0).length
                : mensajes.filter((m) => Number(m.estado) === 1).length;

            return (
              <Chip
                key={key}
                label={`${label} (${count})`}
                size="small"
                onClick={() => setActiveFilter(key)}
                variant={activeFilter === key ? "filled" : "outlined"}
                color={activeFilter === key ? "primary" : "default"}
                sx={{ fontSize: 12, cursor: "pointer" }}
              />
            );
          })}
        </Box>

        {/* Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569", width: 40 }} />
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }}>Remitente</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }}>Asunto / Mensaje</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, color: "#475569" }} align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <MailIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1, display: "block", mx: "auto" }} />
                    <Typography variant="body2" color="text.secondary">
                      No hay mensajes
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((msg) => {
                const isUnread = Number(msg.estado) === 0;
                const isExpanded = expandedId === msg.mensajes_id;

                return (
                  <>
                    <TableRow
                      key={msg.mensajes_id}
                      hover
                      onClick={() => handleRowClick(msg.mensajes_id)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: isUnread ? "rgba(59,130,246,0.04)" : "inherit",
                        borderLeft: isUnread ? "3px solid #3b82f6" : "3px solid transparent",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                      }}
                    >
                      {/* Indicator */}
                      <TableCell sx={{ py: 1.5 }}>
                        {isUnread && (
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: "#3b82f6",
                              mx: "auto",
                            }}
                          />
                        )}
                      </TableCell>

                      {/* Sender */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: 13,
                              fontWeight: 700,
                              bgcolor: isUnread ? "#3b82f6" : "#94a3b8",
                            }}
                          >
                            {getInitial(msg.emisor)}
                          </Avatar>
                          <Typography
                            variant="body2"
                            fontWeight={isUnread ? 700 : 400}
                            sx={{ fontSize: 13 }}
                          >
                            {msg.emisor}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Subject + preview */}
                      <TableCell sx={{ py: 1.5, maxWidth: 340 }}>
                        <Typography
                          variant="body2"
                          fontWeight={isUnread ? 700 : 500}
                          sx={{ fontSize: 13, color: "#0f172a" }}
                          noWrap
                        >
                          {msg.referencia}
                        </Typography>
                        {!isExpanded && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {msg.mensaje}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography
                          variant="caption"
                          color={isUnread ? "#1d4ed8" : "text.secondary"}
                          fontWeight={isUnread ? 600 : 400}
                        >
                          {relativeDate(msg.fecha)}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                          <Tooltip title={isUnread ? "Marcar como leído" : "Marcar como no leído"}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); toggleReadStatus(msg.mensajes_id); }}
                              sx={{ color: "#94a3b8", "&:hover": { color: "#1d4ed8" } }}
                            >
                              {isUnread ? (
                                <MarkReadIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <MarkUnreadIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); deleteMensaje(msg.mensajes_id); }}
                              sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded message body */}
                    {isExpanded && (
                      <TableRow key={`${msg.mensajes_id}-body`}>
                        <TableCell colSpan={5} sx={{ py: 0, px: 0 }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <Box
                              sx={{
                                px: 3,
                                py: 2,
                                bgcolor: "#f8fafc",
                                borderTop: "1px solid #f1f5f9",
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, color: "#334155" }}>
                                {msg.mensaje}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
                                {moment(msg.fecha).format("DD/MM/YYYY HH:mm")}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
